import { useCallback } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";
import { ScheduleDragHandle } from "../../schedule/ScheduleDragHandle";
import { useCustomArtStore, type CustomArt } from "../../../stores/custom-art-store";
import { dispatchScheduleEvent } from "../../../lib/schedule-history/dispatch";
import { cn } from "../../../lib/utils";

type ArtLayerRowProps = { layer: CustomArt };

export function ArtLayerRow({ layer }: ArtLayerRowProps) {
  const { t } = useTranslation();
  const selected = useCustomArtStore(state => state.selectedId === layer.imageId);
  const selectLayer = useCustomArtStore(state => state.selectLayer);
  const draggable = useDraggable({ id: layer.imageId });
  const droppable = useDroppable({ id: layer.imageId });
  const setDragRef = draggable.setNodeRef;
  const setDropRef = droppable.setNodeRef;
  const setNodeRef = useCallback((node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  }, [setDragRef, setDropRef]);
  const transform = draggable.transform;
  const style = { transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined };
  const isDropTarget = droppable.isOver && !draggable.isDragging;
  const row = { ...draggable, setNodeRef, style, isDropTarget };
  const visibilityLabel = t(layer.visible ? "art.hideLayer" : "art.showLayer");

  return (
    <div ref={setNodeRef} style={style} onClick={() => selectLayer(layer.imageId)} className={cn(
      "relative flex min-w-0 items-center gap-2 rounded border border-neutral-200 bg-white p-2 transition-colors",
      selected && "border-neutral-400 bg-neutral-50",
      !layer.visible && "opacity-60 hover:opacity-100 focus-within:opacity-100",
      draggable.isDragging && "z-10 opacity-100 shadow-sm ring-1 ring-neutral-300",
      isDropTarget && "ring-2 ring-neutral-900 ring-offset-2",
    )}>
      <ScheduleDragHandle row={row} label={t("art.dragLayer")} className="h-8 self-stretch" />
      <button type="button" onClick={() => selectLayer(layer.imageId)} title={layer.name}
        className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <img src={layer.image.src} alt="" className="size-8 shrink-0 rounded border border-neutral-200 bg-neutral-100 object-contain" />
        <span className="truncate text-xs font-medium text-neutral-800">{layer.name}</span>
      </button>
      <div className="flex shrink-0 gap-1">
        <Button type="button" variant="ghost" size="icon-xs" title={visibilityLabel}
          className="text-neutral-500 hover:text-neutral-900" onClick={event => {
            event.stopPropagation();
            dispatchScheduleEvent({ type: "art/setVisibility", imageId: layer.imageId, visible: !layer.visible });
          }}>
          {layer.visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
        </Button>
        <Button type="button" variant="ghost" size="icon-xs" title={t("art.removeLayer")}
          className="text-neutral-400 hover:bg-red-50 hover:text-red-600" onClick={event => {
            event.stopPropagation();
            dispatchScheduleEvent({ type: "art/remove", imageId: layer.imageId });
          }}><Trash2 className="size-3.5" /></Button>
      </div>
    </div>
  );
}
